import { useAtom } from '@reatom/npm-react'
import { AnimatePresence, motion } from 'motion/react'
import styled, { css } from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { Trend } from '@root/entities/trend'
import * as TrendUI from '@root/entities/trend/ui'
import { FlashTextContentDistinct } from '@root/features/flash/flash-text-content-distinct'
import { CountMax3DigitsFormatted } from '@root/shared/core/format'

import { TextCell } from './explore-text-cell'
import { ExploreTwoRowsTextCell } from './explore-two-rows-text-cell'

import type { PairMetrics, PairModel } from '@root/entities/pair'

export const HoldersCell = ({
  pair,
  period,
}: {
  pair: PairModel
  period: PairMetrics.MetricsSpan
}) => {
  const [holdersCountTrendDistinct] = useAtom(pair.ui.holdersCountTrendDistinct)
  // model
  const [holdersCount] = useAtom(pair.holdersCount)
  const [holdersChange] = useAtom(pair.stats[period].holdersChange)

  const holdersTrend =
    holdersCountTrendDistinct === undefined
      ? Trend.Neutral
      : Trend.fromOrdering(holdersCountTrendDistinct)

  const changeTrend = Trend.of(holdersChange)

  const renderCount = () => {
    return (
      <FirstRow>
        <FlashTextContentDistinct>
          <TextCell>
            <CountMax3DigitsFormatted count={holdersCount} />
          </TextCell>
        </FlashTextContentDistinct>
        <AnimatePresence>
          {holdersCountTrendDistinct ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              css={css`
                display: grid;
              `}
            >
              <TrendUI.TrendTriangle trend={holdersTrend} size={12} />
            </motion.div>
          ) : undefined}
        </AnimatePresence>
      </FirstRow>
    )
  }

  if (period === 'ALL') {
    return renderCount()
  }

  return (
    <ExploreTwoRowsTextCell
      firstRow={renderCount()}
      secondRow={
        <FlashTextContentDistinct>
          <Trend_ $trend={changeTrend}>
            <Numeric forceSign value={holdersChange} preset="percents" />%
          </Trend_>
        </FlashTextContentDistinct>
      }
    />
  )
}

const Trend_ = styled.span<{ $trend: Trend.Trend }>`
  color: ${({ $trend }) => TrendUI.colorCSSVar($trend, { mode: 'candle' })};
`

const FirstRow = styled.div`
  justify-content: start;
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  gap: 1px;

  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
`
